import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, FormControl, InputLabel, Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logError } from "@src/appV2/lib/analytics/log";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { useConfirmVisit } from "@src/appV2/redesign/InHome/BookedVisitDetails/useConfirmVisit";
import {
  BottomSheet,
  Input,
  PageHeaderWrapper,
  Text,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useHistory } from "react-router-dom";

import { LoadingButton } from "../../components/LoadingButton";
import { useInHomeVisitModalsDataContext } from "../../ShiftDiscovery/useInHomeVisitModalsDataContext";
import {
  BOOKED_VISIT_CONFIRM_DATE_CONFIRMED_MODAL_PATH,
  BOOKED_VISIT_CONFIRM_DATE_MODAL_PATH,
} from "../paths";

interface ConfirmVisitFinalizeBottomSheetProps {
  modalState: UseModalState;
  visitDate: Date;
}

export function ConfirmVisitFinalizeBottomSheet(props: ConfirmVisitFinalizeBottomSheetProps) {
  const { modalState, visitDate } = props;
  const { generateModalPath, visit } = useInHomeVisitModalsDataContext();
  const history = useHistory();
  const { showErrorToast } = useToast();
  const { mutateAsync: confirmVisit, isLoading: isConfirmVisitLoading } = useConfirmVisit(
    visit?.id ?? ""
  );

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical">
          <Stack spacing={3} direction="row" justifyContent="space-between" width="100%">
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                history.replace(
                  generateModalPath(BOOKED_VISIT_CONFIRM_DATE_MODAL_PATH, {
                    caseId: visit?.attributes.caseId ?? "",
                    visitId: visit?.id ?? "",
                  })
                );
              }}
            >
              Back
            </Button>
            <LoadingButton
              fullWidth
              variant="contained"
              isLoading={isConfirmVisitLoading}
              onClick={async () => {
                if (!isDefined(visit)) {
                  return;
                }

                const confirmVisitResult = await confirmVisit(
                  {
                    data: {
                      type: "visit",
                      attributes: { confirmedVisitDate: visitDate.toISOString() },
                    },
                  },
                  {
                    onError: (error) => {
                      logError(
                        APP_V2_APP_EVENTS.IN_HOME_CONFIRMED_VISIT_ERROR,
                        { error, metadata: { redesign: true } },
                        false
                      );
                      showErrorToast("Unable to confirm visit date. Please try again.");
                    },
                  }
                );
                if (isDefined(confirmVisitResult?.id)) {
                  history.replace(
                    generateModalPath(BOOKED_VISIT_CONFIRM_DATE_CONFIRMED_MODAL_PATH, {
                      caseId: visit.attributes.caseId,
                      visitId: visit.id,
                    })
                  );
                }
              }}
            >
              Confirm
            </LoadingButton>
          </Stack>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack spacing={4} alignItems="center">
          <PageHeaderWrapper variant="tertiary" />
          <Title semibold variant="h3" component="h3">
            Confirm visit
          </Title>
          <Text variant="body1">Please review the details of your visit before submitting.</Text>
          <FormControl fullWidth>
            <InputLabel>Visit Date</InputLabel>
            <Input
              disabled
              size="large"
              iconType="calendar-with-cells"
              label="Visit Date"
              value={visitDate.toLocaleDateString()}
            />
          </FormControl>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
